/**
 * Manufacturers — super-admin management of marketplace sellers, plus the one
 * global platform cut (marketplace stage 1, docs/planning/MARKETPLACE_PLAN.md).
 *
 * A manufacturer owns boards in the catalog and sets their prices through a
 * manufacturer_admin account (created on the Users page). Simkura is a row
 * like any other so its own hardware takes the same path.
 */
import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import styled from '@emotion/styled';
import { IconPlus, IconX } from '@tabler/icons-react';
import { useAuth } from '../context/AuthContext';
import { isSuperAdmin } from '../services/users';
import {
  getManufacturers,
  createManufacturer,
  updateManufacturer,
  deleteManufacturer,
} from '../services/manufacturers';
import type { Manufacturer, ManufacturerInput } from '../services/manufacturers';
import { getMarketplaceSettings, updateMarketplaceSettings, formatBps } from '../services/platformSettings';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  Button,
  TableContainer,
  StyledTable,
  Th,
  Td,
  StatusChip,
  LoadingText,
  EmptyState,
  MutedText,
  Caption,
  Row,
  Stack,
  ModalOverlay,
  ModalCard,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  TextArea,
  HelperText,
  ErrorAlert,
  Checkbox,
  H2Title,
  DetailCard,
  SectionTitle,
} from '../components/styled';

const ProviderLink = styled(Link)`
  color: ${({ theme }) => theme.accent.primary};
  text-decoration: none;
  font-weight: ${({ theme }) => theme.font.weight.semiBold};

  &:hover { text-decoration: underline; }
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 ${({ theme }) => theme.spacing(3)};
`;

/* No component selectors here: this project has no @emotion/babel-plugin,
   so `${FormGroup} {}` throws at render. The field group is styled inline. */
const FeeRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

const FeeField = styled(FormGroup)`
  margin-bottom: 0;
  min-width: 160px;
`;

// SectionTitle has no top margin and DetailCard no bottom margin, so a title
// after a card sits flush against it. Same fix CompanyDetail uses.
const SpacedSectionTitle = styled(SectionTitle)`
  margin-top: ${({ theme }) => theme.spacing(8)};
`;

const FeeValue = styled.span`
  font-size: ${({ theme }) => theme.font.size.xl};
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  color: ${({ theme }) => theme.font.color.primary};
`;

interface ManufacturerForm {
  name: string;
  slug: string;
  support_email: string;
  website: string;
  active: boolean;
  notes: string;
  tagline: string;
  about: string;
}

const emptyForm = (): ManufacturerForm => ({
  name: '', slug: '', support_email: '', website: '', active: true, notes: '', tagline: '', about: '',
});

const formFrom = (m: Manufacturer): ManufacturerForm => ({
  name: m.name,
  slug: m.slug,
  support_email: m.support_email || '',
  website: m.website || '',
  active: m.active,
  notes: m.notes || '',
  tagline: m.tagline || '',
  about: m.about || '',
});

function toInput(form: ManufacturerForm): ManufacturerInput {
  return {
    name: form.name.trim(),
    // Blank slug → the API derives one from the name.
    ...(form.slug.trim() ? { slug: form.slug.trim() } : {}),
    support_email: form.support_email.trim() || null,
    website: form.website.trim() || null,
    active: form.active,
    notes: form.notes.trim() || null,
    tagline: form.tagline.trim() || null,
    about: form.about.trim() || null,
  };
}

function Manufacturers() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [modal, setModal] = useState<'create' | 'edit' | null>(null);
  const [selected, setSelected] = useState<Manufacturer | null>(null);
  const [form, setForm] = useState<ManufacturerForm>(emptyForm());
  const [error, setError] = useState<string | null>(null);
  const [feeInput, setFeeInput] = useState<string | null>(null); // percent as typed
  const [feeError, setFeeError] = useState<string | null>(null);

  if (!isSuperAdmin(user)) {
    return <Navigate to="/admin" replace />;
  }

  const { data: manufacturers = [], isLoading } = useQuery({
    queryKey: ['manufacturers'],
    queryFn: () => getManufacturers(),
  });
  const { data: settings } = useQuery({
    queryKey: ['marketplace-settings'],
    queryFn: getMarketplaceSettings,
  });

  const closeModal = () => {
    setModal(null);
    setSelected(null);
    setError(null);
  };

  const onMutationError = (err: any, fallback: string) => {
    const body = err.response?.data;
    setError(body?.details?.join?.('; ') || body?.error || fallback);
  };

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['manufacturers'] });
    queryClient.invalidateQueries({ queryKey: ['device-boards'] });
  };

  const createMutation = useMutation({
    mutationFn: createManufacturer,
    onSuccess: () => { invalidate(); closeModal(); },
    onError: (err) => onMutationError(err, 'Failed to create manufacturer'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: ManufacturerInput }) => updateManufacturer(id, data),
    onSuccess: () => { invalidate(); closeModal(); },
    onError: (err) => onMutationError(err, 'Failed to update manufacturer'),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteManufacturer,
    onSuccess: invalidate,
    onError: (err) => onMutationError(err, 'Failed to delete manufacturer'),
  });

  const feeMutation = useMutation({
    mutationFn: updateMarketplaceSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketplace-settings'] });
      setFeeInput(null);
      setFeeError(null);
    },
    onError: (err: any) => setFeeError(err.response?.data?.error || 'Failed to update platform fee'),
  });

  const openCreate = () => {
    setForm(emptyForm());
    setModal('create');
    setError(null);
  };

  const openEdit = (m: Manufacturer) => {
    setSelected(m);
    setForm(formFrom(m));
    setModal('edit');
    setError(null);
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const data = toInput(form);
    if (modal === 'edit' && selected) {
      updateMutation.mutate({ id: selected.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const confirmDelete = (m: Manufacturer) => {
    if (m.board_count > 0 || m.user_count > 0) {
      setError(`${m.name} still has ${m.board_count} board(s) and ${m.user_count} user(s); reassign them first.`);
      return;
    }
    if (window.confirm(`Delete ${m.name}?`)) {
      setError(null);
      deleteMutation.mutate(m.id);
    }
  };

  const saveFee = (e: React.FormEvent) => {
    e.preventDefault();
    const pct = Number(feeInput);
    if (feeInput === null || feeInput.trim() === '' || Number.isNaN(pct) || pct < 0 || pct > 100) {
      setFeeError('Enter a percentage between 0 and 100');
      return;
    }
    feeMutation.mutate({ platform_fee_bps: Math.round(pct * 100) });
  };

  const pending = createMutation.isPending || updateMutation.isPending;
  const feePercent = settings ? (settings.platform_fee_bps / 100).toString() : '';

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Hardware Providers</PageTitle>
        <Button variant="primary" onClick={openCreate}>
          <IconPlus size={16} stroke={2} />
          Add Hardware Provider
        </Button>
      </PageHeader>

      <MutedText>
        A hardware provider sells its boards through the platform and sets the
        monthly service fee for each. Boards in the{' '}
        <Link to="/admin/boards">board catalog</Link> belong to one; a manufacturer
        admin account (Users page) lets them price their own boards.
      </MutedText>

      {error && modal === null && <ErrorAlert>{error}</ErrorAlert>}

      <SpacedSectionTitle>Platform cut</SpacedSectionTitle>
      <DetailCard>
        <form onSubmit={saveFee}>
          <FeeRow>
            <div>
              <Caption>Current</Caption>
              <div><FeeValue>{settings ? formatBps(settings.platform_fee_bps) : '…'}</FeeValue></div>
            </div>
            <FeeField>
              <Label>New percentage</Label>
              <Input
                type="number"
                min={0}
                max={100}
                step="0.01"
                value={feeInput ?? feePercent}
                onChange={(e) => { setFeeInput(e.target.value); setFeeError(null); }}
              />
            </FeeField>
            <Button
              variant="primary"
              type="submit"
              disabled={feeMutation.isPending || feeInput === null || feeInput === feePercent}
            >
              {feeMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </FeeRow>
          <HelperText>
            One global percentage, taken out of every manufacturer's hardware price
            and monthly fee. Buyers never see it. Changing it applies to future
            statements only.
          </HelperText>
          {feeError && <ErrorAlert>{feeError}</ErrorAlert>}
          {settings && settings.platform_fee_bps === 0 && (
            <Caption>The cut is currently 0% — set it before the first statement is issued.</Caption>
          )}
        </form>
      </DetailCard>

      <SpacedSectionTitle>Providers</SpacedSectionTitle>
      {isLoading ? (
        <LoadingText>Loading hardware providers...</LoadingText>
      ) : manufacturers.length === 0 ? (
        <EmptyState>
          <p>No hardware providers yet.</p>
        </EmptyState>
      ) : (
        <TableContainer>
          <StyledTable>
            <thead>
              <tr>
                <Th>Provider</Th>
                <Th>Contact</Th>
                <Th>Boards</Th>
                <Th>Deployed devices</Th>
                <Th>Admins</Th>
                <Th>Status</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {manufacturers.map((m) => (
                <tr key={m.id}>
                  <Td>
                    <Stack gap={1}>
                      <ProviderLink to={`/hardware-providers/${m.id}`}>{m.name}</ProviderLink>
                      <Caption>{m.slug}</Caption>
                      {m.notes && <Caption>{m.notes}</Caption>}
                    </Stack>
                  </Td>
                  <Td>
                    <Stack gap={1}>
                      <span>{m.support_email || <MutedText>—</MutedText>}</span>
                      {m.website && <Caption>{m.website}</Caption>}
                    </Stack>
                  </Td>
                  <Td>
                    <Link to={`/hardware-providers/${m.id}?tab=boards`}>{m.board_count}</Link>
                  </Td>
                  <Td>
                    <Link to={`/hardware-providers/${m.id}?tab=devices`}>{m.deployed_device_count}</Link>
                    <Caption> across {m.software_provider_count} software provider{m.software_provider_count === 1 ? '' : 's'}</Caption>
                  </Td>
                  <Td>{m.user_count}</Td>
                  <Td>
                    <StatusChip variant={m.active ? 'positive' : 'neutral'}>
                      {m.active ? 'active' : 'inactive'}
                    </StatusChip>
                  </Td>
                  <Td>
                    <Row gap={2}>
                      <Button size="sm" variant="secondary" onClick={() => openEdit(m)}>Edit</Button>
                      <Button
                        size="sm"
                        variant="danger"
                        disabled={m.board_count > 0 || m.user_count > 0 || deleteMutation.isPending}
                        title={m.board_count > 0 || m.user_count > 0 ? 'Reassign its boards and users first' : undefined}
                        onClick={() => confirmDelete(m)}
                      >
                        Delete
                      </Button>
                    </Row>
                  </Td>
                </tr>
              ))}
            </tbody>
          </StyledTable>
        </TableContainer>
      )}

      {modal && (
        <ModalOverlay onClick={closeModal}>
          <ModalCard size="md" onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <H2Title>{modal === 'edit' ? `Edit ${selected?.name ?? 'hardware provider'}` : 'Add Hardware Provider'}</H2Title>
              <ModalCloseButton onClick={closeModal} aria-label="Close">
                <IconX size={18} stroke={2} />
              </ModalCloseButton>
            </ModalHeader>
            <form onSubmit={submit}>
              <ModalBody>
                {error && <ErrorAlert>{error}</ErrorAlert>}

                <FieldGrid>
                  <FormGroup>
                    <Label>Name</Label>
                    <Input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Mercury Security"
                      required
                    />
                    <HelperText>Shown as <code>device.manufacturer</code> on every board it owns.</HelperText>
                  </FormGroup>
                  <FormGroup>
                    <Label>Slug</Label>
                    <Input
                      value={form.slug}
                      onChange={(e) => setForm({ ...form, slug: e.target.value })}
                      placeholder="derived from name"
                      pattern="[a-z0-9]+(-[a-z0-9]+)*"
                    />
                    <HelperText>Lowercase, digits and hyphens. Leave blank to derive.</HelperText>
                  </FormGroup>
                </FieldGrid>

                <FieldGrid>
                  <FormGroup>
                    <Label>Support email</Label>
                    <Input
                      type="email"
                      value={form.support_email}
                      onChange={(e) => setForm({ ...form, support_email: e.target.value })}
                      placeholder="support@example.com"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Website</Label>
                    <Input
                      type="url"
                      value={form.website}
                      onChange={(e) => setForm({ ...form, website: e.target.value })}
                      placeholder="https://"
                    />
                  </FormGroup>
                </FieldGrid>

                <FormGroup>
                  <Checkbox
                    checked={form.active}
                    onChange={() => setForm({ ...form, active: !form.active })}
                    label="Active — its boards can be listed and priced"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Product overview</Label>
                  <Input
                    value={form.tagline}
                    onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                    placeholder="Cellular smart locks for every door."
                    maxLength={200}
                  />
                  <HelperText>One sentence about what the provider makes. Shown under their name on the public Hardware providers page and the catalog.</HelperText>
                </FormGroup>

                <FormGroup>
                  <Label>Public about</Label>
                  <TextArea
                    value={form.about}
                    onChange={(e) => setForm({ ...form, about: e.target.value })}
                    rows={3}
                    placeholder="Who the provider is, shown on the public catalog. Blank lines start new paragraphs."
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Internal notes</Label>
                  <TextArea
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    rows={3}
                    placeholder="Internal notes: agreement, contacts, caveats"
                  />
                </FormGroup>
              </ModalBody>
              <ModalFooter>
                <Button variant="secondary" type="button" onClick={closeModal}>Cancel</Button>
                <Button variant="primary" type="submit" disabled={pending}>
                  {pending ? 'Saving...' : modal === 'edit' ? 'Save Changes' : 'Add Hardware Provider'}
                </Button>
              </ModalFooter>
            </form>
          </ModalCard>
        </ModalOverlay>
      )}
    </PageContainer>
  );
}

export default Manufacturers;
